import React, { useEffect, useMemo, useState } from 'react';
import EventCard from './EventCard';
import { getEventsByDateRange } from '../../services/events';
import { getLikeCount, getLikeState, likeEvent, unlikeEvent } from '../../services/likes';
import { langField, langTags } from '../../utils/eventLang';
import { useAuth } from '../../context/AuthContext';

// ── date helpers ──────────────────────────────────────────────────────────────

function todayUtcString() {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = String(now.getUTCMonth() + 1).padStart(2, '0');
  const d = String(now.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function addDays(ymd, days) {
  const d = new Date(`${ymd}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

// ── section grouping ──────────────────────────────────────────────────────────

function utcDayMs(isoStr) {
  const d = new Date(isoStr);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function todayMs() {
  const now = new Date();
  return Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
}

// Fixed display order for each mode
const FUTURE_ORDER = ['Today', 'Tomorrow', 'Next Week', 'Next Month'];
const FUTURE_ORDER_ZH = ['今天', '明天', '本周', '下个月'];
const HISTORY_ORDER = ['Yesterday', 'Last Week', 'Last Month', 'Earlier'];
const HISTORY_ORDER_ZH = ['昨天', '上周', '上个月', '更早'];

function getSectionLabel(event, view, lang) {
  const isZh = lang === 'zh';
  const today = todayMs();

  if (view === 'future') {
    const startMs = utcDayMs(event.startAt);
    const endMs   = event.endAt ? utcDayMs(event.endAt) : startMs;

    // Event spans today → Today (regardless of startAt being in the past)
    if (startMs <= today && today <= endMs) return isZh ? '今天' : 'Today';

    const diffStart = Math.round((startMs - today) / 86400000);
    if (diffStart === 1) return isZh ? '明天' : 'Tomorrow';
    if (diffStart <= 7)  return isZh ? '本周' : 'Next Week';
    return isZh ? '下个月' : 'Next Month';
  } else {
    const diffStart = Math.round((utcDayMs(event.startAt) - today) / 86400000);
    if (diffStart === -1) return isZh ? '昨天' : 'Yesterday';
    if (diffStart >= -7)  return isZh ? '上周' : 'Last Week';
    if (diffStart >= -30) return isZh ? '上个月' : 'Last Month';
    return isZh ? '更早' : 'Earlier';
  }
}

function buildSections(events, view, lang) {
  const isZh = lang === 'zh';
  // Pre-create map in fixed display order so sections always appear Today → Tomorrow → …
  const order = view === 'future'
    ? (isZh ? FUTURE_ORDER_ZH : FUTURE_ORDER)
    : (isZh ? HISTORY_ORDER_ZH : HISTORY_ORDER);

  const map = new Map(order.map((label) => [label, []]));

  for (const ev of events) {
    const label = getSectionLabel(ev, view, lang);
    if (map.has(label)) map.get(label).push(ev);
  }

  return Array.from(map.entries())
    .filter(([, items]) => items.length > 0)
    .map(([label, items]) => ({ label, items }));
}

// ── like sorting ─────────────────────────────────────────────────────────────

function eventId(ev) {
  return String(ev.pid ?? ev.eventId);
}

function sortByLikes(items, likeData) {
  const withLikes = [];
  const withoutLikes = [];
  for (const ev of items) {
    const count = likeData[eventId(ev)]?.count ?? 0;
    if (count > 0) withLikes.push({ ev, count });
    else withoutLikes.push(ev);
  }
  withLikes.sort((a, b) => b.count - a.count);
  return [...withLikes.map((x) => x.ev), ...withoutLikes];
}

// ── detail modal helpers ──────────────────────────────────────────────────────

function pad(n) { return String(n).padStart(2, '0'); }

function fmtDetailDate(isoStr, lang) {
  if (!isoStr) return '';
  return new Date(isoStr).toLocaleDateString(lang === 'zh' ? 'zh-CN' : 'en-US', {
    weekday: 'short', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  });
}

function fmtDetailTime(isoStr) {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  const h = d.getUTCHours(), m = d.getUTCMinutes();
  if (h === 0 && m === 0) return '';
  return `${pad(h)}:${pad(m)}`;
}

const CalIconLg = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <rect x="1.5" y="3" width="13" height="11" rx="2" stroke="currentColor" strokeWidth="1.4"/>
    <path d="M1.5 7h13" stroke="currentColor" strokeWidth="1.4"/>
    <path d="M5.5 1.5v3M10.5 1.5v3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
  </svg>
);

const PinIconLg = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M8 1.5C5.515 1.5 3.5 3.515 3.5 6c0 3.75 4.5 8.5 4.5 8.5S12.5 9.75 12.5 6C12.5 3.515 10.485 1.5 8 1.5z"
      stroke="currentColor" strokeWidth="1.4"/>
    <circle cx="8" cy="6" r="1.75" stroke="currentColor" strokeWidth="1.4"/>
  </svg>
);

const TicketIcon = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.4"/>
    <path d="M8 4.5v7M6 6.5h2.5a1.5 1.5 0 0 1 0 3H6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
  </svg>
);

// ── EventDetailModal ──────────────────────────────────────────────────────────

function EventDetailModal({ event, lang, onClose }) {
  const isZh = lang === 'zh';

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const title       = langField(event, 'title', lang);
  const description = langField(event, 'description', lang);
  const category    = langField(event, 'category', lang);
  const locationName     = langField(event, 'locationName', lang);
  const locationDistrict = langField(event, 'locationDistrict', lang);
  const locationAddress  = langField(event, 'locationAddress', lang);
  const sourceName  = langField(event, 'sourceName', lang) || event.sourceName;
  const tags        = langTags(event, lang);

  const startDate = fmtDetailDate(event.startAt, lang);
  const startTime = fmtDetailTime(event.startAt);
  const endDate   = fmtDetailDate(event.endAt, lang);
  const endTime   = fmtDetailTime(event.endAt);
  const showEndDate = endDate && endDate !== startDate;

  let priceText = null;
  if (event.isFree) {
    priceText = isZh ? '免费' : 'Free';
  } else if (event.priceMin != null) {
    const cur = (event.priceCurrency || '').toUpperCase();
    const prefix = cur === 'CNY' ? '¥' : '';
    const suffix = cur === 'CNY' ? '' : (cur ? ` ${cur}` : '');
    if (event.priceMax && event.priceMax !== event.priceMin) {
      priceText = `${prefix}${event.priceMin}–${event.priceMax}${suffix}`.trim();
    } else {
      priceText = `${prefix}${event.priceMin}${suffix}`.trim();
    }
  }

  const heat = event.heatScore >= 80
    ? (isZh ? '热门' : 'Hot')
    : event.heatScore >= 50
    ? (isZh ? '上升' : 'Trending')
    : null;

  return (
    <div className="event-detail-mask" onClick={onClose}>
      <div className="event-detail-panel" onClick={(e) => e.stopPropagation()}>
        <button className="event-detail-close" onClick={onClose} aria-label="Close">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
        </button>

        <div className="event-detail-tags-top">
          {category && <span className="event-tag event-tag-category">{category}</span>}
          {heat && <span className="event-tag event-tag-heat">{heat}</span>}
        </div>

        <h2 className="event-detail-title">{title}</h2>

        <div className="event-detail-row">
          <span className="event-detail-icon"><CalIconLg /></span>
          <div>
            <div>{startDate}{startTime && ` · ${startTime}`}</div>
            {showEndDate && (
              <div className="event-detail-sub">
                {isZh ? '结束：' : 'Ends: '}{endDate}{endTime && ` · ${endTime}`}
              </div>
            )}
          </div>
        </div>

        {(locationName || locationDistrict || locationAddress) && (
          <div className="event-detail-row">
            <span className="event-detail-icon"><PinIconLg /></span>
            <div>
              {locationName && <div>{locationName}</div>}
              {locationDistrict && <div className="event-detail-sub">{locationDistrict}</div>}
              {locationAddress && <div className="event-detail-sub">{locationAddress}</div>}
            </div>
          </div>
        )}

        {priceText && (
          <div className="event-detail-row">
            <span className="event-detail-icon"><TicketIcon /></span>
            <span className={`event-price-badge${event.isFree ? ' is-free' : ''}`}>{priceText}</span>
          </div>
        )}

        {description && (
          <div className="event-detail-desc">
            <div className="event-detail-desc-label">{isZh ? '活动介绍' : 'About'}</div>
            <p>{description}</p>
          </div>
        )}

        {tags.length > 0 && (
          <div className="event-detail-taglist">
            {tags.map((tag) => (
              <span key={tag} className="event-detail-tag">{tag}</span>
            ))}
          </div>
        )}

        {(sourceName || event.sourceUrl) && (
          <div className="event-detail-source">
            {event.sourceUrl ? (
              <a href={event.sourceUrl} target="_blank" rel="noopener noreferrer">
                {sourceName || event.sourceUrl}
              </a>
            ) : (
              <span>{sourceName}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── EventList ─────────────────────────────────────────────────────────────────

function EventList({ lang }) {
  const isEn = lang === 'en';
  const { user } = useAuth();
  const [view, setView] = useState('future');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedCats, setSelectedCats] = useState(new Set());
  const [detailEvent, setDetailEvent] = useState(null);
  // likeData: { [eventId]: { count: number, liked: boolean } }
  const [likeData, setLikeData] = useState({});

  useEffect(() => {
    const today = todayUtcString();
    const startDate = view === 'future' ? today : addDays(today, -90);
    const endDate   = view === 'future' ? addDays(today, 90) : addDays(today, -1);

    setSearch('');
    setSelectedCats(new Set());
    setLikeData({});
    setLoading(true);
    setError(null);
    getEventsByDateRange(startDate, endDate, lang)
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        if (view === 'history') list.reverse();
        setEvents(list);
        return list;
      })
      .then((list) => {
        if (list.length === 0) return;
        const isLoggedIn = !!localStorage.getItem('token');
        const fetcher = isLoggedIn ? getLikeState : getLikeCount;
        Promise.allSettled(list.map((ev) => fetcher(eventId(ev)))).then((results) => {
          const next = {};
          results.forEach((res, i) => {
            const id = eventId(list[i]);
            if (res.status === 'fulfilled') {
              if (isLoggedIn) {
                next[id] = { count: Number(res.value.likeCount), liked: !!res.value.liked };
              } else {
                next[id] = { count: Number(res.value), liked: false };
              }
            } else {
              next[id] = { count: 0, liked: false };
            }
          });
          setLikeData(next);
        });
      })
      .catch((err) => {
        setError(err.message || 'Failed to load');
        setEvents([]);
      })
      .finally(() => setLoading(false));
  }, [view, lang]);

  async function handleLike(ev) {
    const id = eventId(ev);
    const current = likeData[id] ?? { count: 0, liked: false };
    const newLiked = !current.liked;
    const optimisticCount = newLiked
      ? current.count + 1
      : Math.max(0, current.count - 1);

    setLikeData((prev) => ({
      ...prev,
      [id]: { count: optimisticCount, liked: newLiked },
    }));

    try {
      const result = newLiked ? await likeEvent(id) : await unlikeEvent(id);
      setLikeData((prev) => ({
        ...prev,
        [id]: { count: Number(result.likeCount), liked: !!result.liked },
      }));
    } catch {
      setLikeData((prev) => ({ ...prev, [id]: current }));
    }
  }

  const categories = useMemo(() => {
    const cats = new Set();
    events.forEach((ev) => {
      const cat = langField(ev, 'category', lang);
      if (cat) cats.add(cat);
    });
    return Array.from(cats).sort();
  }, [events, lang]);

  const filteredEvents = useMemo(() => {
    let result = events;
    const q = search.toLowerCase().trim();
    if (q) {
      result = result.filter((ev) => {
        const t = (langField(ev, 'title', lang) || '').toLowerCase();
        const d = (langField(ev, 'description', lang) || '').toLowerCase();
        return t.includes(q) || d.includes(q);
      });
    }
    if (selectedCats.size > 0) {
      result = result.filter((ev) => selectedCats.has(langField(ev, 'category', lang)));
    }
    return result;
  }, [events, search, selectedCats, lang]);

  const sections = useMemo(() => {
    const raw = buildSections(filteredEvents, view, lang);
    return raw.map(({ label, items }) => ({
      label,
      items: sortByLikes(items, likeData),
    }));
  }, [filteredEvents, view, lang, likeData]);

  function toggleCat(cat) {
    setSelectedCats((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  }

  return (
    <div className="event-list-container">
      {/* Header */}
      <div className="event-list-header">
        <div>
          <h1 className="event-list-heading">
            {isEn ? 'Shanghai Events' : '上海活动'}
          </h1>
          <p className="event-list-sub">
            {isEn ? "Discover what's happening in Shanghai" : '发现上海最新精彩活动'}
          </p>
        </div>
        <div className="event-view-toggle">
          <button
            type="button"
            className={`toggle-btn${view === 'future' ? ' active' : ''}`}
            onClick={() => setView('future')}
          >{isEn ? 'Future' : '即将'}</button>
          <button
            type="button"
            className={`toggle-btn${view === 'history' ? ' active' : ''}`}
            onClick={() => setView('history')}
          >{isEn ? 'History' : '历史'}</button>
        </div>
      </div>

      {/* Toolbar: search + category filter */}
      <div className="event-toolbar">
        <div className="event-search-wrap">
          <svg className="event-search-icon" width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <circle cx="6.5" cy="6.5" r="5" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M10.5 10.5L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          <input
            type="text"
            className="event-search-input"
            placeholder={isEn ? 'Search events…' : '搜索活动…'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {categories.length > 0 && (
          <div className="event-cat-filter">
            <button
              type="button"
              className={`cat-chip${selectedCats.size === 0 ? ' active' : ''}`}
              onClick={() => setSelectedCats(new Set())}
            >{isEn ? 'All' : '全部'}</button>
            {categories.map((cat) => (
              <button
                key={cat}
                type="button"
                className={`cat-chip${selectedCats.has(cat) ? ' active' : ''}`}
                onClick={() => toggleCat(cat)}
              >{cat}</button>
            ))}
          </div>
        )}
      </div>

      {/* Loading / error / empty states */}
      {loading && (
        <div className="event-list-state">
          <span className="event-loading-spinner" />
          <span>{isEn ? 'Loading events…' : '加载中…'}</span>
        </div>
      )}
      {!loading && error && (
        <div className="event-list-state event-list-error">
          {isEn ? `Error: ${error}` : `加载失败：${error}`}
        </div>
      )}
      {!loading && !error && filteredEvents.length === 0 && (
        <div className="event-list-state">
          {isEn ? 'No events found.' : '暂无活动。'}
        </div>
      )}

      {/* Grouped sections */}
      {!loading && !error && sections.map(({ label, items }) => (
        <div key={label} className="event-section">
          <div className="event-section-divider">
            <span className="event-section-label">{label}</span>
          </div>
          <div className="event-grid">
            {items.map((ev) => {
              const id = eventId(ev);
              const ld = likeData[id] ?? { count: 0, liked: false };
              return (
              <EventCard
                key={ev.pid ?? ev.eventId}
                event={ev}
                lang={lang}
                onClick={() => setDetailEvent(ev)}
                likeCount={ld.count}
                liked={ld.liked}
                canLike={!!user}
                onLike={() => handleLike(ev)}
              />
              );
            })}
          </div>
        </div>
      ))}

      {/* Detail popup */}
      {detailEvent && (
        <EventDetailModal
          event={detailEvent}
          lang={lang}
          onClose={() => setDetailEvent(null)}
        />
      )}
    </div>
  );
}

export default EventList;
